/**
 * AffiliatesServices — agora consome V2 (affiliatesV2API + managersV2API)
 * com shape compatível com a UI legada.
 *
 * Antes (V1):
 *   GET /affiliation/products       → produtos onde sou afiliado OU manager
 *   GET /affiliation/affiliates     → afiliados dos meus produtos
 *   GET /affiliation/pending        → vínculos pendentes (header + tela)
 *
 * Depois (V2):
 *   affiliatesV2API.listMine()      → vínculos onde sou afiliado
 *   managersV2API.listMine()        → vínculos onde sou manager
 *   productsV2API.findById(id)      → dados do produto (enriquece o vínculo)
 *
 * Shape devolvido pra UI:
 *   { success, data: [{ id, role_type: 'affiliate'|'manager',
 *                       commission_rate, status, createdAt,
 *                       product: { id, uuid, name, image, product_code, producer_name } }] }
 */

import { affiliatesV2API } from '../v2/affiliates';
import { managersV2API } from '../v2/managers';
import { productsV2API } from '../v2/products';
import { mapProductV2ToV1 } from '../../../utils/v2ShapeMapper';

/**
 * Busca produto V2 e devolve no shape V1. Faz cache em memória durante a sessão
 * pra evitar N+1 em telas que combinam afiliações/managers do mesmo produto.
 */
const productCache = new Map();
async function fetchProductV1Shape(productId) {
  if (!productId) return null;
  if (productCache.has(productId)) return productCache.get(productId);
  try {
    const res = await productsV2API.findById(productId);
    const raw = res.data?.product || res.data?.data || res.data;
    const v1Shape = mapProductV2ToV1(raw);
    productCache.set(productId, v1Shape);
    return v1Shape;
  } catch (err) {
    console.warn(`Erro carregando produto ${productId} para enriquecer vínculo:`, err.message);
    return null;
  }
}

/**
 * Converte um vínculo V2 em item shape V1.
 */
async function normalizeLink(link, roleType) {
  const product = await fetchProductV1Shape(link.product_id);
  return {
    id: link.id,
    role_type: roleType,
    commission_rate: link.commission_value_override ?? product?.commission_value_affiliate ?? null,
    status: link.status,
    affiliate_code: link.affiliate_code ?? null,
    createdAt: link.created_at,
    can_manage_affiliates: link.can_manage_affiliates ?? false,
    product: product || { id: link.product_id, name: 'Produto', image: null },
  };
}

const AffiliatesServices = {
  /**
   * Lista vínculos como afiliado.
   */
  getAffiliates: async () => {
    try {
      const res = await affiliatesV2API.listMine();
      const list = res.data?.links || res.data?.data || [];
      const normalized = await Promise.all(list.map((l) => normalizeLink(l, 'affiliate')));
      return { success: true, data: normalized };
    } catch (error) {
      console.error('Error in getAffiliates:', error);
      throw error;
    }
  },

  /**
   * "Produtos que Promovo / Gerencio" — combina afiliações + managers.
   * UI filtra por role_type pra separar nas duas páginas.
   */
  getAffiliatesProducts: async () => {
    try {
      const [affRes, mgrRes] = await Promise.all([
        affiliatesV2API.listMine(),
        managersV2API.listMine(),
      ]);

      const affLinks = affRes.data?.links || affRes.data?.data || [];
      const mgrLinks = mgrRes.data?.links || mgrRes.data?.data || [];

      const [normalizedAff, normalizedMgr] = await Promise.all([
        Promise.all(affLinks.map((l) => normalizeLink(l, 'affiliate'))),
        Promise.all(mgrLinks.map((l) => normalizeLink(l, 'manager'))),
      ]);

      return { success: true, data: [...normalizedAff, ...normalizedMgr] };
    } catch (error) {
      console.error('Error in getAffiliatesProducts:', error);
      throw error;
    }
  },

  /**
   * Vínculos pendentes do user (convites de afiliação aguardando aceite).
   * Usado no header e em "Minhas Solicitações".
   */
  getAffiliatesPending: async () => {
    try {
      const res = await affiliatesV2API.listMine({ status: 'pending' });
      const list = res.data?.links || res.data?.data || [];
      const normalized = await Promise.all(list.map((l) => normalizeLink(l, 'affiliate')));
      return { success: true, data: normalized };
    } catch (error) {
      console.error('Error in getAffiliatesPending:', error);
      throw error;
    }
  },

  /**
   * Aceitar convite de afiliação.
   * V1: POST /affiliation/accept/:id
   * V2: POST /api/v2/affiliates/:id/approve  (mesmo efeito do ponto de vista do user)
   */
  acceptAffiliate: async (affiliateId) => {
    try {
      const res = await affiliatesV2API.approve(affiliateId);
      return res.data;
    } catch (error) {
      console.error('Error in acceptAffiliate:', error);
      throw error;
    }
  },
};

export default AffiliatesServices;
